/**
 * Priority Selector Component
 * 
 * Allows agents and admins to manually set conversation priority levels.
 * Uses specific color scheme requested:
 * - Urgent: Rose #E11D48
 * - High: Amber #F59E0B  
 * - Normal: Emerald #10B981
 * - Low: Indigo #6366F1
 * 
 * @version 1.0.0
 * @author CleverAct AI Team
 */

import React, { useState } from 'react'
import { Button } from '../../ui/Button'
import { Badge } from '../../ui/Badge'
import { 
  ChevronDown,
  Check,
  AlertTriangle,
  TrendingUp,
  Minus,
  ArrowDown
} from 'lucide-react'
import { api } from '../../../services/api'
import { logger } from '../../../utils/logger'

/**
 * Priority configuration with exact color scheme
 */
export const PRIORITY_CONFIG = {
  urgent: {
    label: 'Urgent',
    value: 'urgent',
    icon: AlertTriangle,
    textColor: '#E11D48', // Rose 600
    backgroundColor: '#FFE4E6', // Rose 100
    borderColor: '#E11D48',
    description: 'Requires immediate attention'
  },
  high: {
    label: 'High',
    value: 'high', 
    icon: TrendingUp,
    textColor: '#F59E0B', // Amber 500
    backgroundColor: '#FEF3C7', // Amber 100
    borderColor: '#F59E0B',
    description: 'Important, handle soon'
  },
  normal: {
    label: 'Normal',
    value: 'normal',
    icon: Minus,
    textColor: '#10B981', // Emerald 500
    backgroundColor: '#D1FAE5', // Emerald 100
    borderColor: '#10B981',
    description: 'Standard priority'
  },
  low: {
    label: 'Low',
    value: 'low',
    icon: ArrowDown,
    textColor: '#6366F1', // Indigo 500
    backgroundColor: '#E0E7FF', // Indigo 100
    borderColor: '#6366F1',
    description: 'Can be handled later'
  }
} as const

export type PriorityLevel = keyof typeof PRIORITY_CONFIG

/**
 * Priority Badge Component
 */
export const PriorityBadge: React.FC<{
  priority: PriorityLevel
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
}> = ({ 
  priority, 
  size = 'md', 
  showIcon = true,
  className = ''
}) => {
  const config = PRIORITY_CONFIG[priority]
  const Icon = config.icon

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5', 
    lg: 'text-base px-4 py-2'
  }

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <Badge
      style={{
        color: config.textColor,
        backgroundColor: config.backgroundColor,
        borderColor: config.borderColor
      }}
      className={`
        border font-medium inline-flex items-center gap-1.5
        ${sizeClasses[size]} ${className}
      `}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {config.label}
    </Badge>
  )
}

/**
 * Priority Selector Props
 */
export interface PrioritySelectorProps {
  /** Current priority level */
  currentPriority: PriorityLevel
  /** Conversation ID to update */
  conversationId: string
  /** Callback when priority changes */
  onPriorityChange?: (newPriority: PriorityLevel, oldPriority: PriorityLevel) => void
  /** Whether user can edit priority */
  canEdit?: boolean
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Custom CSS classes */
  className?: string
  /** Show as dropdown or inline badges */
  variant?: 'dropdown' | 'inline'
}

/**
 * Priority Selector Component
 */
export const PrioritySelector: React.FC<PrioritySelectorProps> = ({
  currentPriority,
  conversationId,
  onPriorityChange,
  canEdit = true,
  size = 'md',
  className = '',
  variant = 'dropdown'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  /**
   * Handle priority update
   */
  const handlePriorityUpdate = async (newPriority: PriorityLevel) => {
    if (!canEdit || newPriority === currentPriority || isUpdating) return

    try {
      setIsUpdating(true)
      setIsOpen(false)

      logger.info('🎯 Updating conversation priority', {
        component: 'PrioritySelector',
        conversationId,
        oldPriority: currentPriority,
        newPriority
      })

      const response = await api.put(`/conversations/${conversationId}/priority`, {
        priority: newPriority
      })

      if (response.data.success) {
        logger.info('✅ Priority updated successfully', {
          component: 'PrioritySelector',
          conversationId,
          oldPriority: currentPriority,
          newPriority
        })

        // Call callback if provided
        onPriorityChange?.(newPriority, currentPriority)
      } else {
        throw new Error(response.data.error || 'Failed to update priority')
      }

    } catch (error) {
      logger.error('❌ Error updating priority', {
        component: 'PrioritySelector',
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      // Could show toast notification here
      console.error('Failed to update priority:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  // Inline variant - shows all priorities as selectable badges
  if (variant === 'inline') {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {Object.entries(PRIORITY_CONFIG).map(([key, config]) => {
          const isSelected = key === currentPriority
          const priorityKey = key as PriorityLevel
          
          return (
            <button
              key={key}
              onClick={() => handlePriorityUpdate(priorityKey)}
              disabled={!canEdit || isUpdating}
              className={`
                transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed
                ${isSelected ? 'ring-2 ring-offset-2' : 'hover:ring-1 hover:ring-offset-1'}
              `}
              style={{
                ringColor: config.borderColor
              }}
            >
              <PriorityBadge priority={priorityKey} size={size} />
            </button>
          )
        })}
      </div>
    )
  }

  // Dropdown variant - shows current priority with dropdown
  return (
    <div className={`relative ${className}`}>
      <Button
        variant="ghost"
        size={size}
        onClick={() => canEdit && setIsOpen(!isOpen)}
        disabled={!canEdit || isUpdating}
        className="h-auto p-0 hover:bg-transparent"
      >
        <div className="flex items-center gap-2">
          <PriorityBadge priority={currentPriority} size={size} />
          {canEdit && (
            <ChevronDown className={`
              w-4 h-4 text-gray-400 transition-transform duration-200
              ${isOpen ? 'rotate-180' : ''}
            `} />
          )}
        </div>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && canEdit && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
            <div className="px-3 py-2 border-b border-gray-100">
              <h4 className="text-sm font-medium text-gray-900">Set Priority</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Choose the conversation priority level
              </p>
            </div>
            
            <div className="py-1">
              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => {
                const isSelected = key === currentPriority
                const priorityKey = key as PriorityLevel
                const Icon = config.icon
                
                return (
                  <button
                    key={key}
                    onClick={() => handlePriorityUpdate(priorityKey)}
                    disabled={isUpdating}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                      hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                      ${isSelected ? 'bg-gray-50' : ''}
                    `}
                  >
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full"
                      style={{
                        backgroundColor: config.backgroundColor,
                        color: config.textColor
                      }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span 
                          className="font-medium"
                          style={{ color: config.textColor }}
                        >
                          {config.label}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-emerald-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {config.description}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PrioritySelector